import Swal from "sweetalert2";
import { exportarCSV } from "../../functions/importar_masivo";
import { actualizarMultiple } from "../../functions/crud";
import { dialogo, escapar } from "./dialogos_cartera";
import { esPorteria } from "../../functions/pase_visitante";

// Piezas que comparten los listados de encomiendas, visitantes y novedades.

const SIN_ESTADO = "Sin estado";

export const esResidente = () => localStorage.getItem("Permisos") === "RESIDENTE";

// Un residente solo ve los registros de su vivienda.
export const soloDelResidente = (lista, campoDireccion) => {
  if (!esResidente()) return lista;
  const propia = (localStorage.getItem("Direccion") || "").trim().toUpperCase();
  return lista.filter(item => (item[campoDireccion] || "").trim().toUpperCase() === propia);
};

export const filtrarPorEstado = (datos, estado) =>
  estado ? datos.filter(fila => (fila.estado || SIN_ESTADO) === estado) : datos;

// Botón único de cada fila de la tabla (columna ACCIONES).
export const botonOpciones = (accion) => [{ texto: "Opciones", accion }];

const estiloBotonArchivos = { padding: "7px 14px", margin: 2, backgroundColor: "#c0392b", color: "#fff", border: "none", borderRadius: 7, fontSize: "0.82rem", fontWeight: 500, cursor: "pointer" };

export const BotonArchivos = ({ onClick }) => (
  <button onClick={onClick} style={estiloBotonArchivos} title="Exportar, plantilla y carga desde Excel">
    <i className="bi bi-file-earmark-spreadsheet" />
  </button>
);

const estiloChip = (activo) => ({
  padding: "6px 14px",
  borderRadius: 20,
  background: activo ? "#0d6efd" : "#f0f0f0",
  color: activo ? "#fff" : "#000",
  fontWeight: "bold",
  fontSize: 14,
  cursor: "pointer",
  userSelect: "none",
});

// Conteo por estado (cada chip filtra la tabla) y, a la derecha, los botones del listado.
export const BarraEstados = ({ datos, filtro, onFiltrar, children }) => {
  const conteo = datos.reduce((acc, fila) => {
    const estado = fila.estado || SIN_ESTADO;
    acc[estado] = (acc[estado] || 0) + 1;
    return acc;
  }, {});
  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "12px 0", alignItems: "center" }}>
      {Object.entries(conteo).map(([estado, cantidad]) => (
        <div
          key={estado}
          onClick={() => onFiltrar(filtro === estado ? null : estado)}
          title="Clic para filtrar por este estado"
          style={estiloChip(filtro === estado)}
        >
          {estado}: {cantidad}
        </div>
      ))}
      <div style={{ marginLeft: "auto", display: "flex", gap: 0 }}>{children}</div>
    </div>
  );
};

export const mostrarFotos = (titulo, urls, textoSinFotos) => {
  if (!urls.length) {
    Swal.fire("Sin fotos", textoSinFotos, "info");
    return;
  }
  Swal.fire({
    title: titulo,
    width: 700,
    html: `<div style="display:flex;gap:10px;flex-wrap:wrap;justify-content:center;">
      ${urls.map(u => `<a href="${u}" target="_blank" rel="noopener">
        <img src="${u}" style="width:150px;height:150px;object-fit:cover;border-radius:8px;border:1px solid #e2e6ea;">
      </a>`).join("")}
    </div>`,
    confirmButtonText: "Cerrar",
  });
};

// Abre el menú de archivos y hace lo elegido. cargar() abre el asistente y devuelve true si guardó.
export const abrirMenuArchivos = async ({ menu, columnas, filas, archivoCsv, descargarPlantilla, cargar }) => {
  const opcion = await menu({ puedeCargar: esPorteria() });
  if (opcion === "exportar") exportarCSV(columnas, filas, archivoCsv);
  if (opcion === "plantilla") descargarPlantilla();
  return opcion === "cargar" ? cargar() : false;
};

// ---------- Eliminar registros (solo la cuenta de sistemas) ----------

const CORREO_ELIMINAR = "sistemas@coordinarseguridad.com";

export const puedeEliminar = () =>
  (localStorage.getItem("CorreoElectronico") || "").trim().toLowerCase() === CORREO_ELIMINAR;

// Tarjeta "Eliminar" del menú de opciones: para los demás usuarios se ve en gris y no se puede elegir.
export const opcionEliminar = (singular) => {
  const permitido = puedeEliminar();
  return {
    clave: "eliminar",
    icono: "bi-trash",
    nombre: "Eliminar",
    detalle: permitido
      ? `Borra ${singular} para siempre. No se puede deshacer.`
      : "Solo la cuenta de sistemas puede eliminar registros.",
    bloqueada: !permitido,
  };
};

// Pide confirmación y borra el registro en la ruta exacta donde se leyó (fila.rutaRegistro, que ya
// incluye el año y el mes de la carpeta). rutasExtra son otras rutas ligadas que también se borran.
// Devuelve true si se eliminó.
export const eliminarConConfirmacion = async ({ fila, singular, descripcion, rutasExtra = [] }) => {
  if (!puedeEliminar()) return false;
  // Nunca borra una carpeta de año o mes (hay datos sueltos guardados así por error).
  if (!fila.rutaRegistro || /\/\d{2,4}$/.test(fila.rutaRegistro)) {
    await dialogo({ icono: "bi-exclamation-circle", tono: "naranja", titulo: "No se puede eliminar",
      texto: "No se encontró dónde está guardado este registro. Recargue la página e intente de nuevo." });
    return false;
  }
  const { isConfirmed } = await dialogo({
    icono: "bi-trash",
    tono: "rojo",
    titulo: `¿Eliminar ${singular}?`,
    texto: `<strong>${escapar(descripcion)}</strong><br>Se borra para siempre, también en la app. No se puede deshacer.`,
    confirmar: "Sí, eliminar",
    iconoConfirmar: "bi-trash",
    cancelar: "Cancelar",
  });
  if (!isConfirmed) return false;
  try {
    await actualizarMultiple(Object.fromEntries([fila.rutaRegistro, ...rutasExtra].map(ruta => [ruta, null])));
    Swal.fire({ title: "Eliminado", text: `Se eliminó ${singular}.`, icon: "success", timer: 1800, showConfirmButton: false });
    return true;
  } catch (error) {
    await dialogo({ icono: "bi-exclamation-circle", tono: "naranja", titulo: "No se pudo eliminar", texto: escapar(error.message) });
    return false;
  }
};
